import { useEffect, useState, useCallback } from "react";
import api from "../../api/client";

function StatCard({ label, valor, sub, color = "#6366f1" }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">{label}</p>
      <p className="text-2xl font-bold" style={{ color }}>{valor}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function whatsappUrl(telefono, nombre) {
  const tel = (telefono || "").replace(/\D/g, "");
  if (!tel) return null;
  const msg = encodeURIComponent(`Hola ${nombre}, te contactamos desde nuestra tienda.`);
  return `https://wa.me/${tel}?text=${msg}`;
}

export default function Clientes() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [page, setPage] = useState(1);
  const [busqueda, setBusqueda] = useState("");
  const [busquedaActiva, setBusquedaActiva] = useState("");
  const [loading, setLoading] = useState(true);
  const PAGE_SIZE = 20;

  const cargar = useCallback((b = busquedaActiva, p = page) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p, page_size: PAGE_SIZE });
    if (b) params.append("busqueda", b);
    api.get(`/clientes/?${params.toString()}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [busquedaActiva, page]);

  useEffect(() => { cargar(busquedaActiva, page); }, [page, busquedaActiva]);

  const buscar = () => { setBusquedaActiva(busqueda); setPage(1); };
  const limpiar = () => { setBusqueda(""); setBusquedaActiva(""); setPage(1); };

  const eliminar = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar a ${nombre} de la lista de clientes?`)) return;
    await api.delete(`/clientes/${id}`);
    cargar();
  };

  const totalPages = Math.ceil(data.total / PAGE_SIZE);

  const totalGastado = data.items.reduce((s, c) => s + Number(c.total_gastado || 0), 0);
  const totalPedidos = data.items.reduce((s, c) => s + (c.total_pedidos || 0), 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Clientes</h2>
        <p className="text-gray-500 text-sm mt-1">
          Clientes registrados automaticamente al realizar un pedido
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard label="Total clientes" valor={data.total} color="#6366f1" />
        <StatCard label="Pedidos (pagina)" valor={totalPedidos} color="#8b5cf6" />
        <StatCard
          label="Facturado (pagina)"
          valor={`$${totalGastado.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          color="#10b981"
        />
      </div>

      {/* Buscador */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <div className="flex gap-3">
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && buscar()}
            placeholder="Buscar por nombre o telefono..."
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            onClick={buscar}
            className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            Buscar
          </button>
          {busquedaActiva && (
            <button
              onClick={limpiar}
              className="border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : data.items.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <div className="text-5xl mb-3">👥</div>
            <p className="font-medium">
              {busquedaActiva ? "Sin resultados" : "Aun no hay clientes registrados"}
            </p>
            <p className="text-sm mt-1">
              {busquedaActiva
                ? "Intenta con otro nombre o telefono"
                : "Los clientes se registran automaticamente al hacer un pedido en el catalogo"}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                  <tr>
                    <th className="px-5 py-3 text-left">Cliente</th>
                    <th className="px-5 py-3 text-left">Telefono</th>
                    <th className="px-5 py-3 text-right">Pedidos</th>
                    <th className="px-5 py-3 text-right">Total gastado</th>
                    <th className="px-5 py-3 text-left hidden md:table-cell">Ultima compra</th>
                    <th className="px-5 py-3 text-left hidden lg:table-cell">Registro</th>
                    <th className="px-5 py-3 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.items.map((c) => {
                    const wp = whatsappUrl(c.telefono, c.nombre);
                    const ultimaCompra = c.ultima_compra
                      ? new Date(c.ultima_compra).toLocaleDateString("es-VE")
                      : "—";
                    const registro = c.creado_en
                      ? new Date(c.creado_en).toLocaleDateString("es-VE")
                      : "—";
                    return (
                      <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm flex-shrink-0">
                              {c.nombre.trim().charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium text-gray-800">{c.nombre}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-gray-500">
                          {c.telefono || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-indigo-50 text-indigo-700 font-bold text-xs">
                            {c.total_pedidos}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right font-semibold text-gray-800">
                          ${Number(c.total_gastado).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-5 py-3 text-gray-500 hidden md:table-cell">{ultimaCompra}</td>
                        <td className="px-5 py-3 text-gray-400 text-xs hidden lg:table-cell">{registro}</td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-center gap-2">
                            {wp && (
                              <a
                                href={wp}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-800 transition-colors"
                                title="Abrir WhatsApp"
                              >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                                  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.532 5.862L.054 23.272a.75.75 0 00.918.919l5.594-1.464A11.938 11.938 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.92 0-3.72-.494-5.287-1.356l-.38-.218-3.936 1.031 1.055-3.836-.234-.393A9.955 9.955 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
                                </svg>
                                WhatsApp
                              </a>
                            )}
                            <button
                              onClick={() => eliminar(c.id, c.nombre)}
                              className="text-xs text-red-400 hover:text-red-600 font-medium transition-colors"
                            >
                              Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t text-sm text-gray-500">
                <span>Pagina {page} de {totalPages} — {data.total} clientes</span>
                <div className="flex gap-2">
                  <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-40">Anterior</button>
                  <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-40">Siguiente</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
